import type {
  FullMenuInput,
  ListingMenuKind,
  ListingMenusDto,
  LunchMenuInput,
} from '@pulsify/shared/dto'
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { listingMenusApi } from '~/entities/listing-menu/api/listing-menus-api'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'
import { MINUTES } from '~/shared/lib/time-constants'

/** Both menus of a place as saved, including a Mittagsmenü guests do not see today. */
export function useListingMenus(businessId: string, listingId: string) {
  return useQuery({
    queryKey: queryKey(QueryKey.LISTING_MENUS, listingId),
    queryFn: () => listingMenusApi.get(businessId, listingId),
    staleTime: MINUTES,
    enabled: !!businessId && !!listingId,
  })
}

function patchMenus(
  queryClient: QueryClient,
  listingId: string,
  patch: (menus: ListingMenusDto) => ListingMenusDto,
) {
  queryClient.setQueryData<ListingMenusDto>(queryKey(QueryKey.LISTING_MENUS, listingId), (menus) =>
    menus ? patch(menus) : menus,
  )
  queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.LISTING_MENUS, listingId) })
  queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.LISTING_BUTTONS, listingId) })
}

export function useSaveLunchMenu(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ listingId, input }: { listingId: string; input: LunchMenuInput }) =>
      listingMenusApi.saveLunch(businessId, listingId, input),
    onSuccess: (lunch, { listingId }) => {
      patchMenus(queryClient, listingId, (menus) => ({ ...menus, lunch }))
    },
  })
}

export function useSaveFullMenu(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ listingId, input }: { listingId: string; input: FullMenuInput }) =>
      listingMenusApi.saveFull(businessId, listingId, input),
    onSuccess: (full, { listingId }) => {
      patchMenus(queryClient, listingId, (menus) => ({ ...menus, full }))
    },
  })
}

export function useDeleteListingMenu(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ listingId, kind }: { listingId: string; kind: ListingMenuKind }) =>
      listingMenusApi.remove(businessId, listingId, kind),
    onSuccess: (_data, { listingId, kind }) => {
      patchMenus(queryClient, listingId, (menus) =>
        kind === 'lunch' ? { ...menus, lunch: null } : { ...menus, full: null },
      )
    },
  })
}
